import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { debounce } from 'lodash'
import { Icon } from '@iconify/react'
import { Modal, Select, Spin } from 'antd'

import { showActionNotification } from '../../components/notification'
import { loginState } from '../login/login.slice'
import FoF from '../404/404.index'
import {
    getStoryMapMigrationStatus,
    listStoryMapMigrationSourceProjects,
    listStoryMapMigrationTargetProjects,
    runStoryMapMigration,
} from './storyMapMigration.service'
import {
    canAccessStoryMapMigration,
    getMigrationMessageLabel,
    getMigrationResultMeta,
    getMigrationStatusMeta,
    hasMissingRefs,
    MIGRATION_COUNT_LABELS,
    MIGRATION_MISSING_REF_LABELS,
    resolveCanMigrate,
    resolveMigrationDisplay,
} from './storyMapMigration.utils'
import styles from './StoryMapMigrationPage.module.css'

const STATUS_TONE_CLASS = {
    neutral: styles.statusNeutral,
    info: styles.statusInfo,
    warning: styles.statusWarning,
    success: styles.statusSuccess,
}

const SOURCE_STATS = [
    { key: 'storyMapCount', label: 'Story maps' },
    { key: 'pendingStoryMapCount', label: 'Pending' },
    { key: 'migratedStoryMapCount', label: 'Migrated' },
    { key: 'journeyCount', label: 'Journeys' },
    { key: 'storyCount', label: 'Stories' },
    { key: 'commentCount', label: 'Comments' },
]

const TARGET_STATS = [
    { key: 'projectKey', label: 'Project key', format: (v) => v || '—' },
    {
        key: 'createdFromSupportx',
        label: 'SupportX',
        format: (v) => (v ? 'Yes' : 'No'),
    },
    {
        key: 'hasMigratedStoryMap',
        label: 'Has data',
        format: (v) => (v ? 'Yes' : 'No'),
    },
    { key: 'migratedStoryMapCount', label: 'Migrated maps' },
]

const MigrationStatusBadge = ({ status, className = '' }) => {
    const meta = getMigrationStatusMeta(status)
    return (
        <span
            className={`${styles.statusBadge} ${
                STATUS_TONE_CLASS[meta.tone] || STATUS_TONE_CLASS.info
            } ${className}`}
        >
            {meta.label}
        </span>
    )
}

const StatRow = ({ items, data }) => (
    <div className={styles.statRow}>
        {items.map(({ key, label, format }) => (
            <div key={key} className={styles.statChip}>
                <span className={styles.statValue}>
                    {format ? format(data?.[key]) : (data?.[key] ?? 0)}
                </span>
                <span className={styles.statLabel}>{label}</span>
            </div>
        ))}
    </div>
)

const renderCountGroup = (title, counts = {}, valueClassName = '') => (
    <div>
        <p className={styles.countGroupTitle}>{title}</p>
        <div className={styles.countGrid}>
            {Object.entries(MIGRATION_COUNT_LABELS).map(([key, label]) => (
                <div key={key} className={styles.countCard}>
                    <span className={styles.countLabel}>{label}</span>
                    <span className={`${styles.countValue} ${valueClassName}`}>
                        {counts?.[key] ?? 0}
                    </span>
                </div>
            ))}
        </div>
    </div>
)

const StoryMapMigrationPage = () => {
    const { userProfile } = useSelector(loginState)

    const canAccess = useMemo(
        () => canAccessStoryMapMigration(userProfile),
        [userProfile]
    )

    const [sourceSearch, setSourceSearch] = useState('')
    const [targetSearch, setTargetSearch] = useState('')
    const [sourceProjects, setSourceProjects] = useState([])
    const [targetProjects, setTargetProjects] = useState([])
    const [sourceLoading, setSourceLoading] = useState(false)
    const [targetLoading, setTargetLoading] = useState(false)
    const [statusLoading, setStatusLoading] = useState(false)
    const [selectedSourceId, setSelectedSourceId] = useState(null)
    const [selectedTargetId, setSelectedTargetId] = useState(null)
    const [pairStatus, setPairStatus] = useState(null)
    const [previewing, setPreviewing] = useState(false)
    const [moving, setMoving] = useState(false)
    const [previewResult, setPreviewResult] = useState(null)
    const [moveResult, setMoveResult] = useState(null)

    const selectedSource = useMemo(
        () =>
            sourceProjects.find(
                (item) => String(item._id) === String(selectedSourceId)
            ),
        [selectedSourceId, sourceProjects]
    )

    const selectedTarget = useMemo(
        () =>
            targetProjects.find(
                (item) => String(item._id) === String(selectedTargetId)
            ),
        [selectedTargetId, targetProjects]
    )

    const migrationDisplay = useMemo(
        () =>
            resolveMigrationDisplay({
                pairStatus,
                selectedSource,
                selectedTargetId,
            }),
        [pairStatus, selectedSource, selectedTargetId]
    )

    const canMigrate = useMemo(
        () =>
            resolveCanMigrate({
                pairStatus,
                selectedSource,
                selectedTargetId,
            }),
        [pairStatus, selectedSource, selectedTargetId]
    )

    const {
        isPairScoped,
        migrationStatus: displayStatus,
        pendingStoryMapCount: pendingCount = 0,
        migratedStoryMapCount: migratedCount = 0,
        migratedTo,
    } = migrationDisplay

    const priorMigrationElsewhere =
        selectedTargetId &&
        selectedSource?.migratedTo?.pmsProjectId &&
        String(selectedSource.migratedTo.pmsProjectId) !== String(selectedTargetId)

    const loadSourceProjects = useCallback(async (search) => {
        setSourceLoading(true)
        try {
            const result = await listStoryMapMigrationSourceProjects({ search })
            if (!result.success) {
                showActionNotification({
                    message: result.message || 'Failed to load source projects',
                })
                setSourceProjects([])
                return
            }
            setSourceProjects(result.data ?? [])
        } finally {
            setSourceLoading(false)
        }
    }, [])

    const loadTargetProjects = useCallback(async (search) => {
        setTargetLoading(true)
        try {
            const result = await listStoryMapMigrationTargetProjects({ search })
            if (!result.success) {
                showActionNotification({
                    message: result.message || 'Failed to load target projects',
                })
                setTargetProjects([])
                return
            }
            setTargetProjects(result.data ?? [])
        } finally {
            setTargetLoading(false)
        }
    }, [])

    const loadPairStatus = useCallback(async (sourceId, targetId) => {
        if (!sourceId) {
            setPairStatus(null)
            return
        }

        setStatusLoading(true)
        try {
            const result = await getStoryMapMigrationStatus({
                sourceStoryMapProjectId: String(sourceId),
                targetPmsProjectId: targetId ? String(targetId) : undefined,
            })

            if (!result.success) {
                setPairStatus(null)
                return
            }

            setPairStatus(result.result)
        } finally {
            setStatusLoading(false)
        }
    }, [])

    const debouncedLoadSource = useMemo(
        () => debounce((search) => loadSourceProjects(search), 300),
        [loadSourceProjects]
    )

    const debouncedLoadTarget = useMemo(
        () => debounce((search) => loadTargetProjects(search), 300),
        [loadTargetProjects]
    )

    const patchMigrationStateFromResult = useCallback((result) => {
        if (!result) return

        setPairStatus((prev) => ({
            ...(prev || {}),
            migrationStatus: result.migrationStatus,
            canMigrate: result.canMigrate,
            migratedStoryMapCount: result.migratedStoryMapCount,
            pendingStoryMapCount: result.pendingStoryMapCount,
            isMigrated: result.isMigrated,
        }))

        if (!selectedSourceId) return

        setSourceProjects((prev) =>
            prev.map((project) =>
                String(project._id) === String(selectedSourceId)
                    ? {
                          ...project,
                          migrationStatus: result.migrationStatus ?? project.migrationStatus,
                          canMigrate: result.canMigrate ?? project.canMigrate,
                          migratedStoryMapCount:
                              result.migratedStoryMapCount ?? project.migratedStoryMapCount,
                          pendingStoryMapCount:
                              result.pendingStoryMapCount ?? project.pendingStoryMapCount,
                          isMigrated: result.isMigrated ?? project.isMigrated,
                      }
                    : project
            )
        )

        if (!selectedTargetId) return

        const createdStoryMaps = result.created?.storyMap ?? 0
        if (createdStoryMaps > 0) {
            setTargetProjects((prev) =>
                prev.map((project) =>
                    String(project._id) === String(selectedTargetId)
                        ? {
                              ...project,
                              hasMigratedStoryMap: true,
                              migratedStoryMapCount:
                                  (project.migratedStoryMapCount ?? 0) +
                                  createdStoryMaps,
                          }
                        : project
                )
            )
        }
    }, [selectedSourceId, selectedTargetId])

    useEffect(() => {
        if (!canAccess) return undefined
        loadSourceProjects('')
        loadTargetProjects('')
        return () => {
            debouncedLoadSource.cancel()
            debouncedLoadTarget.cancel()
        }
    }, [
        canAccess,
        debouncedLoadSource,
        debouncedLoadTarget,
        loadSourceProjects,
        loadTargetProjects,
    ])

    useEffect(() => {
        if (!canAccess) return undefined
        debouncedLoadSource(sourceSearch)
        return () => debouncedLoadSource.cancel()
    }, [canAccess, debouncedLoadSource, sourceSearch])

    useEffect(() => {
        if (!canAccess) return undefined
        debouncedLoadTarget(targetSearch)
        return () => debouncedLoadTarget.cancel()
    }, [canAccess, debouncedLoadTarget, targetSearch])

    useEffect(() => {
        if (!canAccess || !selectedSourceId) {
            setPairStatus(null)
            return undefined
        }

        loadPairStatus(selectedSourceId, selectedTargetId)
    }, [canAccess, loadPairStatus, selectedSourceId, selectedTargetId])

    const canSubmit = Boolean(selectedSourceId && selectedTargetId)
    const isBusy = previewing || moving || statusLoading
    const hasPreview = Boolean(previewResult)

    const showMigrationToast = (result, fallbackMessage) => {
        const message =
            getMigrationMessageLabel(result?.messageKey, '') ||
            fallbackMessage ||
            'Migration completed'

        showActionNotification({ message })
    }

    const handlePreview = async () => {
        if (!canSubmit || isBusy) return

        setPreviewing(true)
        setMoveResult(null)
        try {
            const result = await runStoryMapMigration({
                sourceStoryMapProjectId: String(selectedSourceId),
                targetPmsProjectId: String(selectedTargetId),
                dryRun: true,
            })

            if (!result.success) {
                showActionNotification({
                    message: result.message || 'Failed to generate preview',
                })
                return
            }

            setPreviewResult(result.result)
            showMigrationToast(result.result, result.message)
            patchMigrationStateFromResult(result.result)
        } finally {
            setPreviewing(false)
        }
    }

    const executeMove = async () => {
        if (!canSubmit || isBusy || !canMigrate) return

        setMoving(true)
        try {
            const result = await runStoryMapMigration({
                sourceStoryMapProjectId: String(selectedSourceId),
                targetPmsProjectId: String(selectedTargetId),
                dryRun: false,
            })

            if (!result.success) {
                showActionNotification({
                    message: result.message || 'Migration failed',
                })
                return
            }

            setMoveResult(result.result)
            setPreviewResult(null)
            showMigrationToast(result.result, result.message)
            patchMigrationStateFromResult(result.result)
        } finally {
            setMoving(false)
        }
    }

    const handleMove = () => {
        if (!canSubmit || isBusy || !canMigrate) return

        Modal.confirm({
            title: 'Move story map data to PMS project?',
            content: (
                <div className={styles.confirmContent}>
                    <div className={styles.confirmRow}>
                        <Icon
                            className={styles.confirmRowIcon}
                            icon="mdi:database-clock-outline"
                            width={18}
                            height={18}
                        />
                        <div>
                            <span className={styles.confirmRowLabel}>Source</span>
                            <p className={styles.confirmRowValue}>
                                {selectedSource?.name || '—'}
                            </p>
                        </div>
                    </div>
                    <div className={styles.confirmRow}>
                        <Icon
                            className={styles.confirmRowIcon}
                            icon="mdi:folder-arrow-right-outline"
                            width={18}
                            height={18}
                        />
                        <div>
                            <span className={styles.confirmRowLabel}>Target</span>
                            <p className={styles.confirmRowValue}>
                                {selectedTarget?.name || '—'}
                                {selectedTarget?.projectKey
                                    ? ` (${selectedTarget.projectKey})`
                                    : ''}
                            </p>
                        </div>
                    </div>
                    <p className={styles.confirmNote}>
                        Legacy data in xyzed-prod is read-only and will not be
                        modified. Records already migrated will be skipped safely.
                    </p>
                </div>
            ),
            okText: 'Move to PMS Project',
            cancelText: 'Cancel',
            centered: true,
            onOk: executeMove,
        })
    }

    if (!canAccess) {
        return <FoF />
    }

    const activeResult = moveResult || previewResult
    const activeResultMeta = getMigrationResultMeta(activeResult?.migrationResult)

    const renderSourceOption = (project) => {
        const statusMeta = getMigrationStatusMeta(project.migrationStatus)
        return {
            value: String(project._id),
            label: project.name || 'Untitled project',
            project,
            statusMeta,
        }
    }

    const sourceOptions = sourceProjects.map(renderSourceOption)
    const targetOptions = targetProjects.map((project) => ({
        value: String(project._id),
        label: project.projectKey
            ? `${project.name} (${project.projectKey})`
            : project.name || 'Untitled project',
        project,
    }))

    return (
        <div className={styles.page}>
            <header className={styles.pageHeader}>
                <div className={styles.headerRow}>
                    <div className={styles.headerIconWrap}>
                        <Icon icon="mdi:database-arrow-right" width={22} height={22} />
                    </div>
                    <div className={styles.headerText}>
                        <h1 className={styles.title}>Story Map Migration</h1>
                        <p className={styles.subtitle}>
                            Copy story map data from a legacy project into an existing
                            PMS project. Preview first, then move when ready.
                        </p>
                    </div>
                </div>
            </header>

            <div className={styles.pageContent}>
                <div className={styles.introCard}>
                    <div className={styles.introIconWrap}>
                        <Icon icon="mdi:shield-check-outline" width={18} height={18} />
                    </div>
                    <div className={styles.introText}>
                        <p className={styles.introTitle}>Safe, idempotent migration</p>
                        <p className={styles.introDesc}>
                            Source data is never modified. Skip checks apply per
                            target PMS project — the same source can be migrated to
                            different targets. Re-runs only copy what is not yet on
                            the selected target.
                        </p>
                    </div>
                </div>

                {selectedSourceId ? (
                    <section className={styles.statusBanner}>
                        <div className={styles.statusBannerHead}>
                            <h2 className={styles.statusBannerTitle}>
                                Migration status
                                {isPairScoped ? ' (for selected target)' : ''}
                                {statusLoading ? (
                                    <span style={{ marginLeft: 8 }}>
                                        <Spin size="small" />
                                    </span>
                                ) : null}
                            </h2>
                            <MigrationStatusBadge status={displayStatus} />
                        </div>
                        <div className={styles.statusBannerGrid}>
                            <div className={styles.statusMetric}>
                                <span className={styles.statusMetricLabel}>
                                    Pending story maps
                                </span>
                                <span className={styles.statusMetricValue}>
                                    {pendingCount}
                                </span>
                            </div>
                            <div className={styles.statusMetric}>
                                <span className={styles.statusMetricLabel}>
                                    Already migrated
                                </span>
                                <span className={styles.statusMetricValue}>
                                    {migratedCount}
                                </span>
                            </div>
                            <div className={styles.statusMetric}>
                                <span className={styles.statusMetricLabel}>
                                    Can migrate
                                </span>
                                <span className={styles.statusMetricValue}>
                                    {canMigrate ? 'Yes' : 'No'}
                                </span>
                            </div>
                        </div>
                        {priorMigrationElsewhere ? (
                            <p className={styles.statusBannerNote}>
                                This source was previously migrated to{' '}
                                <strong>
                                    {selectedSource.migratedTo.pmsProjectName}
                                </strong>
                                {selectedSource.migratedTo.projectKey
                                    ? ` (${selectedSource.migratedTo.projectKey})`
                                    : ''}
                                . Status above reflects the currently selected target
                                only.
                            </p>
                        ) : migratedTo?.pmsProjectName ? (
                            <p className={styles.statusBannerNote}>
                                Migrated to{' '}
                                <strong>{migratedTo.pmsProjectName}</strong>
                                {migratedTo.projectKey
                                    ? ` (${migratedTo.projectKey})`
                                    : ''}
                            </p>
                        ) : null}
                    </section>
                ) : null}

                <section className={styles.workflowCard}>
                    <div className={styles.workflowHeader}>
                        <h2 className={styles.workflowTitle}>Select source and target</h2>
                        <p className={styles.workflowHint}>
                            Names do not need to match — pick the pair you want to link.
                        </p>
                    </div>

                    <div className={styles.panelsWrapper}>
                        <div className={`${styles.panel} ${styles.panelSource}`}>
                            <div className={styles.panelHead}>
                                <div
                                    className={`${styles.panelIconWrap} ${styles.panelIconSource}`}
                                >
                                    <Icon
                                        icon="mdi:database-clock-outline"
                                        width={18}
                                        height={18}
                                    />
                                </div>
                                <div className={styles.panelHeadText}>
                                    <h3 className={styles.panelTitle}>
                                        Old Story Map Project
                                    </h3>
                                    <p className={styles.panelHint}>
                                        Source from xyzed-prod
                                    </p>
                                </div>
                            </div>

                            <label
                                className={styles.fieldLabel}
                                htmlFor="migration-source"
                            >
                                Source project
                            </label>
                            <Select
                                id="migration-source"
                                className={styles.select}
                                showSearch
                                allowClear
                                placeholder="Search legacy project…"
                                value={selectedSourceId}
                                loading={sourceLoading}
                                filterOption={false}
                                onSearch={setSourceSearch}
                                onChange={(value) => {
                                    setSelectedSourceId(value ?? null)
                                    setPreviewResult(null)
                                    setMoveResult(null)
                                    setPairStatus(null)
                                }}
                                options={sourceOptions}
                                optionRender={(option) => {
                                    const project = option.data?.project
                                    const statusMeta = option.data?.statusMeta
                                    return (
                                        <div className={styles.optionRow}>
                                            <span className={styles.optionName}>
                                                {option.label}
                                            </span>
                                            {project ? (
                                                <span className={styles.optionMeta}>
                                                    {statusMeta?.label} ·{' '}
                                                    {project.storyMapCount ?? 0} maps
                                                </span>
                                            ) : null}
                                        </div>
                                    )
                                }}
                                notFoundContent={
                                    sourceLoading ? (
                                        <Spin size="small" />
                                    ) : (
                                        'No projects found'
                                    )
                                }
                            />

                            {selectedSource ? (
                                <div className={styles.metaCard}>
                                    <div className={styles.metaCardHead}>
                                        <p className={styles.metaName}>
                                            {selectedSource.name}
                                        </p>
                                        <MigrationStatusBadge
                                            status={
                                                isPairScoped
                                                    ? displayStatus
                                                    : selectedSource.migrationStatus
                                            }
                                        />
                                    </div>
                                    <StatRow
                                        items={SOURCE_STATS}
                                        data={
                                            isPairScoped
                                                ? {
                                                      ...selectedSource,
                                                      pendingStoryMapCount:
                                                          pendingCount,
                                                      migratedStoryMapCount:
                                                          migratedCount,
                                                  }
                                                : selectedSource
                                        }
                                    />
                                </div>
                            ) : (
                                <div className={styles.panelEmpty}>
                                    <Icon
                                        icon="mdi:map-search-outline"
                                        width={28}
                                        height={28}
                                    />
                                    <p className={styles.panelEmptyText}>
                                        Select a legacy story map project to see counts
                                        and migration status.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className={styles.arrowConnector} aria-hidden="true">
                            <Icon icon="mdi:arrow-right-bold" width={28} height={28} />
                        </div>

                        <div className={`${styles.panel} ${styles.panelTarget}`}>
                            <div className={styles.panelHead}>
                                <div
                                    className={`${styles.panelIconWrap} ${styles.panelIconTarget}`}
                                >
                                    <Icon
                                        icon="mdi:folder-arrow-right-outline"
                                        width={18}
                                        height={18}
                                    />
                                </div>
                                <div className={styles.panelHeadText}>
                                    <h3 className={styles.panelTitle}>
                                        PMS Project (target)
                                    </h3>
                                    <p className={styles.panelHint}>
                                        Existing support workspace project
                                    </p>
                                </div>
                            </div>

                            <label
                                className={styles.fieldLabel}
                                htmlFor="migration-target"
                            >
                                Target project
                            </label>
                            <Select
                                id="migration-target"
                                className={styles.select}
                                showSearch
                                allowClear
                                placeholder="Search PMS project…"
                                value={selectedTargetId}
                                loading={targetLoading}
                                filterOption={false}
                                onSearch={setTargetSearch}
                                onChange={(value) => {
                                    setSelectedTargetId(value ?? null)
                                    setPreviewResult(null)
                                    setMoveResult(null)
                                }}
                                options={targetOptions}
                                optionRender={(option) => {
                                    const project = option.data?.project
                                    return (
                                        <div className={styles.optionRow}>
                                            <span className={styles.optionName}>
                                                {option.label}
                                            </span>
                                            {project?.hasMigratedStoryMap ? (
                                                <span className={styles.optionMeta}>
                                                    Has migrated data
                                                </span>
                                            ) : null}
                                        </div>
                                    )
                                }}
                                notFoundContent={
                                    targetLoading ? (
                                        <Spin size="small" />
                                    ) : (
                                        'No projects found'
                                    )
                                }
                            />

                            {selectedTarget ? (
                                <div className={styles.metaCard}>
                                    <div className={styles.metaCardHead}>
                                        <p className={styles.metaName}>
                                            {selectedTarget.name}
                                        </p>
                                        {selectedTarget.projectKey ? (
                                            <span
                                                className={`${styles.statusBadge} ${styles.statusInfo}`}
                                            >
                                                {selectedTarget.projectKey}
                                            </span>
                                        ) : null}
                                    </div>
                                    <StatRow
                                        items={TARGET_STATS}
                                        data={selectedTarget}
                                    />
                                </div>
                            ) : (
                                <div className={styles.panelEmpty}>
                                    <Icon
                                        icon="mdi:folder-search-outline"
                                        width={28}
                                        height={28}
                                    />
                                    <p className={styles.panelEmptyText}>
                                        Select the PMS project that should receive the
                                        migrated story map data.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.workflowFooter}>
                        <div className={styles.actionBar}>
                            <div className={styles.actionSteps}>
                                <span
                                    className={`${styles.actionStep} ${
                                        selectedSourceId && selectedTargetId
                                            ? styles.actionStepDone
                                            : styles.actionStepActive
                                    }`}
                                >
                                    <Icon icon="mdi:numeric-1-circle" width={14} height={14} />
                                    Select projects
                                </span>
                                <span
                                    className={`${styles.actionStep} ${
                                        hasPreview ? styles.actionStepDone : ''
                                    } ${canSubmit && !hasPreview ? styles.actionStepActive : ''}`}
                                >
                                    <Icon icon="mdi:numeric-2-circle" width={14} height={14} />
                                    Preview
                                </span>
                                <span
                                    className={`${styles.actionStep} ${
                                        moveResult ? styles.actionStepDone : ''
                                    }`}
                                >
                                    <Icon icon="mdi:numeric-3-circle" width={14} height={14} />
                                    Move
                                </span>
                            </div>

                            <div className={styles.actionButtons}>
                                <button
                                    type="button"
                                    className={styles.previewBtn}
                                    disabled={!canSubmit || isBusy}
                                    onClick={handlePreview}
                                >
                                    {previewing ? (
                                        <Spin size="small" />
                                    ) : (
                                        <Icon icon="mdi:eye-outline" width={18} height={18} />
                                    )}
                                    Preview
                                </button>
                                <button
                                    type="button"
                                    className={styles.moveBtn}
                                    disabled={!canSubmit || isBusy || !canMigrate}
                                    onClick={handleMove}
                                    title={
                                        !canMigrate
                                            ? 'Nothing left to migrate for this source/target pair'
                                            : undefined
                                    }
                                >
                                    {moving ? (
                                        <Spin size="small" />
                                    ) : (
                                        <Icon
                                            icon="mdi:database-arrow-right"
                                            width={18}
                                            height={18}
                                        />
                                    )}
                                    Move to PMS Project
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                <div className={styles.tipsRow}>
                    <div className={styles.tipCard}>
                        <Icon
                            className={styles.tipIcon}
                            icon="mdi:tag-outline"
                            width={16}
                            height={16}
                        />
                        <p className={styles.tipText}>
                            Status is scoped to the selected target — a source
                            migrated elsewhere can still migrate to a new target.
                        </p>
                    </div>
                    <div className={styles.tipCard}>
                        <Icon
                            className={styles.tipIcon}
                            icon="mdi:eye-check-outline"
                            width={16}
                            height={16}
                        />
                        <p className={styles.tipText}>
                            Always preview before moving. Skipped items were already
                            migrated.
                        </p>
                    </div>
                    <div className={styles.tipCard}>
                        <Icon
                            className={styles.tipIcon}
                            icon="mdi:lock-outline"
                            width={16}
                            height={16}
                        />
                        <p className={styles.tipText}>
                            If counts look wrong after move, check missing references
                            in the result summary.
                        </p>
                    </div>
                </div>

                {activeResult ? (
                    <section className={styles.resultSection}>
                        <div className={styles.resultHeader}>
                            <div className={styles.resultHeaderTop}>
                                <div>
                                    <h3 className={styles.resultTitle}>
                                        {activeResult.dryRun
                                            ? 'Preview summary'
                                            : 'Migration summary'}
                                    </h3>
                                    <p className={styles.resultSubtitle}>
                                        {activeResult.sourceProject?.name || 'Source'} →{' '}
                                        {activeResult.targetProject?.name || 'Target'}
                                        {activeResult.targetProject?.projectKey
                                            ? ` (${activeResult.targetProject.projectKey})`
                                            : ''}
                                    </p>
                                </div>
                                <span
                                    className={`${styles.resultTypeBadge} ${
                                        activeResult.dryRun
                                            ? ''
                                            : styles.resultTypeBadgeDone
                                    }`}
                                >
                                    <Icon
                                        icon={
                                            activeResult.dryRun
                                                ? 'mdi:eye-outline'
                                                : 'mdi:check-circle-outline'
                                        }
                                        width={14}
                                        height={14}
                                    />
                                    {activeResult.dryRun ? 'Preview' : 'Completed'}
                                </span>
                            </div>
                        </div>

                        <div className={styles.resultSummaryRow}>
                            <div
                                className={`${styles.resultSummaryCard} ${styles.resultSummaryCardHighlight}`}
                            >
                                <span className={styles.resultSummaryLabel}>
                                    {activeResult.dryRun ? 'Will create' : 'Created'}
                                </span>
                                <span className={styles.resultSummaryValue}>
                                    {activeResult.totalCreated ?? 0}
                                </span>
                            </div>
                            <div
                                className={`${styles.resultSummaryCard} ${styles.resultSummaryCardWarning}`}
                            >
                                <span className={styles.resultSummaryLabel}>Skipped</span>
                                <span className={styles.resultSummaryValue}>
                                    {activeResult.totalSkipped ?? 0}
                                </span>
                            </div>
                            <div className={styles.resultSummaryCard}>
                                <span className={styles.resultSummaryLabel}>Result</span>
                                <span
                                    className={styles.resultSummaryValue}
                                    style={{ fontSize: 16 }}
                                >
                                    {activeResultMeta?.label || '—'}
                                </span>
                            </div>
                        </div>

                        <div className={styles.resultBody}>
                            {activeResultMeta ? (
                                <div className={styles.resultFlags}>
                                    <span
                                        className={`${styles.statusBadge} ${
                                            STATUS_TONE_CLASS[activeResultMeta.tone] ||
                                            STATUS_TONE_CLASS.info
                                        }`}
                                    >
                                        {activeResultMeta.label}
                                    </span>
                                    {activeResult.isDuplicateRun ? (
                                        <span className={styles.resultFlag}>
                                            Duplicate run
                                        </span>
                                    ) : null}
                                </div>
                            ) : null}

                            {activeResult.messageKey ? (
                                <p className={styles.resultMessage}>
                                    {getMigrationMessageLabel(
                                        activeResult.messageKey,
                                        activeResult.messageKey
                                    )}
                                </p>
                            ) : null}

                            {hasMissingRefs(activeResult.missingRefs) ? (
                                <p className={styles.resultWarning}>
                                    Some records were skipped due to missing parent
                                    references. Review the missing reference counts
                                    below.
                                </p>
                            ) : null}

                            {renderCountGroup(
                                activeResult.dryRun ? 'Will create' : 'Created',
                                activeResult.created
                            )}
                            {renderCountGroup(
                                'Skipped (already migrated)',
                                activeResult.skipped,
                                styles.countValueSkipped
                            )}
                            <div>
                                <p className={styles.countGroupTitle}>
                                    Missing references
                                </p>
                                <div className={styles.countGrid}>
                                    {Object.entries(MIGRATION_MISSING_REF_LABELS).map(
                                        ([key, label]) => (
                                            <div key={key} className={styles.countCard}>
                                                <span className={styles.countLabel}>
                                                    {label}
                                                </span>
                                                <span
                                                    className={`${styles.countValue} ${styles.countValueMissing}`}
                                                >
                                                    {activeResult.missingRefs?.[key] ?? 0}
                                                </span>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                ) : null}
            </div>
        </div>
    )
}

export default StoryMapMigrationPage
